import React, { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function ChangePasswordPage() {
  const { user, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Self-guard: users who have already changed their password should not
  // land here (e.g. after re-login with the new password).
  if (user && !user.mustChangePassword) {
    return <Navigate to="/dashboard" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 8) return setError("New password must be at least 8 characters");
    if (next !== confirm) return setError("Passwords don't match");
    setBusy(true);
    try {
      await api.changePassword(current, next);
      toast.success("Password changed · please sign in again");
      logout();
    } catch (err) {
      setError(err?.response?.data?.detail || "Change failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-[#F8F9FA]" data-testid="change-password-page">
      <form onSubmit={submit} className="w-full max-w-sm ab-card ab-shadow-sm p-5 flex flex-col gap-4">
        <div className="flex flex-col items-center text-center gap-1">
          <img src="/brand/ndm-logo.jpg" alt="NDM" className="h-12 w-12 rounded-lg object-cover" />
          <h1 className="mt-2 text-base font-semibold text-[#0F172A]">Set a new password</h1>
          <p className="text-xs text-[#64748B]">
            Welcome {user?.name || user?.email}. Please choose a new password before continuing.
          </p>
        </div>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[#334155]">Current (temporary) password</span>
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="h-10 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
            data-testid="cp-current"
            required
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[#334155]">New password (min 8 chars)</span>
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="h-10 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
            data-testid="cp-new"
            required
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-[#334155]">Confirm new password</span>
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="h-10 px-3 rounded-md border border-[#E2E8F0] bg-white text-sm"
            data-testid="cp-confirm"
            required
          />
        </label>
        {error && (
          <div className="text-xs text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-md px-3 py-2">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="cp-submit"
          className="h-10 rounded-md bg-[#0F172A] text-white text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Change password
        </button>
      </form>
    </div>
  );
}
